import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { errorMessage } from "../../lib/api";
import useIsMobile from "../../hooks/useIsMobile";
import { STAGE_CHART_COLORS, STAGE_LABEL, STAGES, PROSPECT_SOURCE_LABELS } from "../../lib/pipeline";
import { formatPercent, plural, sortedEntries, todoLines } from "../../lib/dashboard";
import { HEALTH_LABELS, healthTone, pilotPath } from "../../lib/pilots";
import { ACTIVITIES, ACTIVITY_LABELS, formatMinutes } from "../../lib/attendance";
import { SEVERITIES, SEVERITY_META } from "../../lib/equipment";
import { formatAmount, formatMinor } from "../../lib/finance";
import { PHASE_LABELS } from "../../lib/customers";
import { formatRate } from "../../lib/marketing";
import { LEVEL_LABELS, formatMoment } from "../../lib/governance";
import { Donut, HBars, RAMP, STATUS, StackedBar, WeekChart } from "../charts/Charts";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import { Skeleton } from "../ui/skeleton";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";

const SOURCE_LABEL = { ...PROSPECT_SOURCE_LABELS, google_sheet: "Google Sheet", google_form: "Google Form", seed: "Exemple", form: "Formulaire" };

// One block's data, loaded on its own: a slow or failing block never holds the others back. `version` changes on « Actualiser ».
export function useBlock(id, version) {
  const [state, setState] = useState({ phase: "loading" });
  const load = useCallback(async () => {
    setState((prev) => (prev.data ? { ...prev, refreshing: true } : { phase: "loading" }));
    try {
      const { data } = await api.get(`/dashboard/blocks/${id}`);
      setState({ phase: "ready", data: data.data });
    } catch (e) {
      // 404 = no longer offered (rights changed meanwhile): the block disappears rather than showing an error.
      if (e?.response?.status === 404) setState({ phase: "gone" });
      else setState((prev) => ({ phase: "error", message: errorMessage(e, "Chargement impossible."), data: prev.data }));
    }
  }, [id]);
  useEffect(() => { load(); }, [load, version]);
  return { ...state, reload: load };
}

function BlockShell({ title, link, children, state, testid, className = "" }) {
  return (
    <Card className={`p-4 flex min-w-0 flex-col ${className}`} data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <h2 className="font-display font-semibold text-slate-900">{title}</h2>
        {link && (
          <Link to={link.to} className="inline-flex items-center gap-0.5 text-xs font-medium text-slate-500 hover:text-slate-900" data-testid={`${testid}-link`}>
            {link.label}<ChevronRight size={14} aria-hidden="true" />
          </Link>
        )}
      </div>
      {state.phase === "loading" && <div className="space-y-2"><Skeleton className="h-5 w-3/4" /><Skeleton className="h-5 w-1/2" /><Skeleton className="h-5 w-2/3" /></div>}
      {state.phase === "error" && !state.data && (
        <div className="flex flex-col items-start gap-2 text-sm text-slate-600" data-testid={`${testid}-error`}>
          <span className="inline-flex items-center gap-1.5"><AlertTriangle size={14} className="text-amber-500" aria-hidden="true" />{state.message}</span>
          <Button variant="outline" size="sm" onClick={state.reload}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </div>
      )}
      {state.data && children(state.data)}
    </Card>
  );
}

// Figures sit two per row on a wide card, so a card stays as short as its chart and neighbours of one row keep similar heights.
function FigureGrid({ children, className = "" }) {
  return <div className={`grid grid-cols-1 gap-x-6 sm:grid-cols-2 ${className}`}>{children}</div>;
}

// A figure on one line; when a list stands behind it, the whole line opens that list.
function Figure({ value, label, to, state, tone = "default", testid }) {
  const content = (
    <>
      <span className={`font-display font-bold text-xl tabular-nums whitespace-nowrap ${tone === "warn" && value > 0 ? "text-red-700" : "text-slate-900"}`}>{value}</span>
      <span className="text-sm text-slate-600">{label}</span>
    </>
  );
  if (!to) return <div className="flex items-baseline gap-2 py-1" data-testid={testid}>{content}</div>;
  return (
    <Link to={to} state={state} data-testid={testid}
      className="flex items-baseline gap-2 py-1 -mx-2 px-2 rounded-md hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      {content}
    </Link>
  );
}

// --- À traiter ---------------------------------------------------------------------------------------------------------------
export function TodoBlock({ version }) {
  const state = useBlock("todo", version);
  if (state.phase === "gone") return null;
  return (
    <BlockShell title="À traiter" state={state} testid="dashboard-todo" className="lg:col-span-2">
      {(data) => {
        const lines = todoLines(data);
        if (!lines.length) {
          return (
            <p className="inline-flex items-center gap-2 text-sm text-slate-600" data-testid="dashboard-todo-empty">
              <CheckCircle2 size={16} className="text-emerald-600" aria-hidden="true" />Rien à traiter pour le moment.
            </p>
          );
        }
        return (
          <ul className="divide-y divide-slate-100">
            {lines.map((line) => (
              <li key={line.id}>
                <Link to={line.to} state={line.state} data-testid={`dashboard-todo-${line.id}`}
                  className="flex items-center justify-between gap-3 py-2 -mx-2 px-2 rounded-md hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <span className="text-sm text-slate-800"><span className="font-semibold tabular-nums">{line.count}</span>{line.text.slice(String(line.count).length)}</span>
                  <ChevronRight size={16} className="text-slate-400 shrink-0" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>
        );
      }}
    </BlockShell>
  );
}

// --- Commercial --------------------------------------------------------------------------------------------------------------
function Breakdown({ title, entries, labels = {} }) {
  if (!entries.length) return null;
  return (
    <div>
      <div className="text-xs font-medium text-slate-500 mb-1">{title}</div>
      <ul className="space-y-1 max-h-40 overflow-y-auto scrollbar-thin">
        {entries.map(([key, value]) => (
          <li key={key} className="flex items-center justify-between text-sm">
            <span className="text-slate-700 truncate">{labels[key] || key}</span>
            <span className="font-semibold tabular-nums text-slate-900">{value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function CommercialBlock({ version }) {
  const state = useBlock("commercial", version);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [details, setDetails] = useState(false);
  if (state.phase === "gone") return null;
  return (
    <BlockShell title="Commercial" link={{ to: "/pipeline", label: "Pipeline" }} state={state} testid="dashboard-commercial" className="lg:col-span-2">
      {(data) => {
        const stageData = STAGES.map((s) => ({ name: s.short, id: s.id, value: data.by_stage[s.id] || 0 }));
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4">
              <Figure value={data.total} label={data.total > 1 ? "prospects" : "prospect"} to="/prospects" testid="dashboard-commercial-total" />
              <Figure value={formatPercent(data.conversion_rate)} label="de conversion" testid="dashboard-commercial-conversion" />
              <Figure value={data.new_this_month} label="nouveaux ce mois" to="/prospects" testid="dashboard-commercial-new" />
              {data.upcoming_meetings !== null && (
                <Figure value={data.upcoming_meetings} label="rendez-vous à venir" to="/tasks" state={{ filters: { type: "rdv" } }}
                  testid="dashboard-commercial-meetings" />
              )}
            </div>
            {data.total === 0 ? (
              <p className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg p-6 text-center" data-testid="dashboard-commercial-empty">
                Aucun prospect pour le moment.
              </p>
            ) : (
              <div className="grid gap-5 lg:grid-cols-3">
                <div className="lg:col-span-2">
                  <div className="mb-1 text-xs font-medium text-slate-500">Où en sont vos prospects</div>
              <div className="h-64" style={{ minHeight: 256 }} data-testid="dashboard-commercial-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stageData}>
                    {/* Nine stages: slanted names on a phone so none overlaps. */}
                    <XAxis dataKey="name" tick={{ fontSize: isMobile ? 10 : 11 }} interval={0}
                      angle={isMobile ? -40 : 0} textAnchor={isMobile ? "end" : "middle"} height={isMobile ? 56 : 30} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} width={32} />
                    <Tooltip />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]} isAnimationActive={false}>
                      {stageData.map((e) => <Cell key={e.id} fill={STAGE_CHART_COLORS[e.id]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
                </div>
                <div className="space-y-4">
                  {data.weekly?.created && (
                    <div>
                      <div className="mb-1 text-xs font-medium text-slate-500">Nouveaux prospects par semaine</div>
                      <WeekChart testid="dashboard-commercial-weekly" height={110} series={[{ key: "created", label: "Nouveaux prospects", color: RAMP[1], points: data.weekly.created }]} />
                    </div>
                  )}
                  <div>
                    <div className="mb-1 text-xs font-medium text-slate-500">D'où ils viennent</div>
                    <Donut testid="dashboard-commercial-sources" size={104} centerLabel="prospects"
                      rows={sortedEntries(data.by_source).map(([key, value]) => ({ id: key, label: SOURCE_LABEL[key] || key, value }))} />
                  </div>
                </div>
              </div>
            )}
            {data.dormant.count > 0 && (
              <div data-testid="dashboard-commercial-dormant">
                <div className="text-sm font-medium text-slate-800 mb-1">
                  {plural(data.dormant.count, "prospect sans activité", "prospects sans activité")} depuis {data.dormant.days} jours
                </div>
                <ul className="divide-y divide-slate-100">
                  {data.dormant.items.slice(0, 5).map((p) => (
                    <li key={p.id}>
                      <button type="button" onClick={() => navigate(`/prospects/${p.id}`)}
                        className="w-full flex items-center justify-between gap-3 py-1.5 text-left text-sm hover:bg-slate-50 -mx-2 px-2 rounded-md">
                        <span className="min-w-0 truncate text-slate-900">{p.pharmacy_name}</span>
                        <span className="shrink-0 text-xs text-slate-500">
                          {STAGE_LABEL[p.status] || p.status} · {p.last_activity_at ? new Date(p.last_activity_at).toLocaleDateString("fr-FR") : "aucune activité"}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {data.total > 0 && (
              <div>
                <button type="button" onClick={() => setDetails((v) => !v)} aria-expanded={details} data-testid="dashboard-commercial-details-toggle"
                  className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900">
                  <ChevronDown size={14} className={`transition-transform ${details ? "rotate-180" : ""}`} aria-hidden="true" />
                  {details ? "Moins de détails" : "Plus de détails"}
                </button>
                {details && (
                  <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="dashboard-commercial-details">
                    <Breakdown title="Par région" entries={sortedEntries(data.by_region)} />
                    {data.by_assignee && (
                      <Breakdown title="Par responsable" entries={sortedEntries(data.by_assignee)}
                        labels={{ ...(data.assignee_names || {}), "non assigné": "Non assigné" }} />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      }}
    </BlockShell>
  );
}

// --- Tâches ------------------------------------------------------------------------------------------------------------------
export function TasksBlock({ version }) {
  const state = useBlock("tasks", version);
  if (state.phase === "gone") return null;
  return (
    <BlockShell title="Tâches" link={{ to: "/tasks", label: "Toutes les tâches" }} state={state} testid="dashboard-tasks">
      {(data) => (
        <div>
          <p className="text-xs text-slate-500 mb-2">{data.scope === "all" ? "Toute l'équipe" : "Mes tâches"}</p>
          {data.weekly?.completed && (
            <div className="mb-3">
              <div className="mb-1 text-xs font-medium text-slate-500">Tâches terminées par semaine</div>
              <WeekChart testid="dashboard-tasks-weekly" height={100} series={[{ key: "completed", label: "Terminées", color: STATUS.good, points: data.weekly.completed }]} />
            </div>
          )}
          <div className="mb-3">
            <HBars testid="dashboard-tasks-chart" rows={[
              { id: "overdue", label: "En retard", value: data.overdue, color: STATUS.critical, keep: true },
              { id: "today", label: "Aujourd'hui", value: data.due_today, keep: true },
              { id: "week", label: "Cette semaine", value: data.due_this_week, keep: true },
              { id: "critical", label: "Critiques ouvertes", value: data.critical_open, color: STATUS.serious, keep: true },
            ]} />
          </div>
          <FigureGrid>
          <Figure value={data.overdue} label="en retard (échéance du jour comprise)" tone="warn" to="/tasks"
            state={{ filters: { due: "overdue" } }} testid="dashboard-tasks-overdue" />
          <Figure value={data.due_today} label="à faire aujourd'hui" to="/tasks" state={{ filters: { due: "today" } }} testid="dashboard-tasks-today" />
          <Figure value={data.due_this_week} label="cette semaine" to="/tasks" state={{ filters: { due: "this_week" } }} testid="dashboard-tasks-week" />
          <Figure value={data.critical_open} label={data.critical_open > 1 ? "critiques ouvertes" : "critique ouverte"} tone="warn" to="/tasks"
            state={{ filters: { priority: "critical" } }} testid="dashboard-tasks-critical" />
          </FigureGrid>
        </div>
      )}
    </BlockShell>
  );
}

// --- Onboardings -------------------------------------------------------------------------------------------------------------
export function OnboardingBlock({ version }) {
  const state = useBlock("onboarding", version);
  const navigate = useNavigate();
  if (state.phase === "gone") return null;
  if (state.phase === "ready" && !state.data.items.length) return null;  // nothing in progress: no empty block
  return (
    <BlockShell title="Onboardings en cours" state={state} testid="dashboard-onboarding">
      {(data) => (
        <ul className="space-y-2 max-h-72 overflow-y-auto scrollbar-thin">
          {data.items.map((o) => (
            <li key={o.id}>
              <button type="button" onClick={() => navigate(`/prospects/${o.id}`)} data-testid={`onboarding-progress-${o.id}`}
                className="w-full text-left p-2 -mx-2 rounded-md hover:bg-slate-50">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="min-w-0 truncate text-sm font-medium text-slate-900">{o.pharmacy_name}</span>
                  <span className="shrink-0 text-sm font-semibold tabular-nums text-jamora">{o.percent} %</span>
                </div>
                <Progress value={o.percent} className="h-1.5" />
                <div className="mt-1 text-xs text-slate-500">Estimé {o.estimated_hours} h · Réel {o.actual_hours} h</div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </BlockShell>
  );
}

// --- Pilotes -----------------------------------------------------------------------------------------------------------------
const PILOT_HEALTH_ORDER = ["blocked", "attention", "unknown", "on_track"];

export function PilotsBlock({ version }) {
  const state = useBlock("pilots", version);
  if (state.phase === "gone") return null;
  if (state.phase === "ready" && !state.data.running) return null;  // no running pilot: nothing to follow here
  return (
    <BlockShell title="Pilotes" link={{ to: "/pilotes", label: "Tous les pilotes" }} state={state} testid="dashboard-pilots">
      {(data) => (
        <div className="space-y-3">
          <div>
            <Figure value={data.running} label={`${data.running > 1 ? "pilotes en cours" : "pilote en cours"}${data.partial ? " (au moins)" : ""}`}
              to="/pilotes" testid="dashboard-pilots-running" />
            {data.ending_soon > 0 && (
              <Figure value={data.ending_soon} label={`${data.ending_soon > 1 ? "se terminent" : "se termine"} dans les ${data.ending_days} jours`}
                to="/pilotes" testid="dashboard-pilots-ending" />
            )}
          </div>
          <Donut testid="dashboard-pilots-chart" centerLabel="pilotes" rows={PILOT_HEALTH_ORDER.map((h) => ({
            id: h, label: HEALTH_LABELS[h], value: data.by_health[h] || 0,
            color: { blocked: STATUS.critical, attention: STATUS.warning, unknown: STATUS.neutral, on_track: STATUS.good }[h] }))} />
          <div className="flex flex-wrap gap-1.5" data-testid="dashboard-pilots-health">
            {PILOT_HEALTH_ORDER.filter((h) => data.by_health[h]).map((h) => (
              <Link key={h} to="/pilotes" state={{ filters: { health: h } }}
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${healthTone(h).chip} hover:opacity-80`}>
                <span className={`h-1.5 w-1.5 rounded-full ${healthTone(h).dot}`} aria-hidden="true" />
                {data.by_health[h]} {HEALTH_LABELS[h].toLowerCase()}
              </Link>
            ))}
          </div>
          {data.at_risk.length > 0 && (
            <ul className="divide-y divide-slate-100" data-testid="dashboard-pilots-at-risk">
              {data.at_risk.map((p) => (
                <li key={p.id}>
                  <Link to={pilotPath(p.prospect_id)} className="flex items-center justify-between gap-3 py-1.5 -mx-2 px-2 rounded-md text-sm hover:bg-slate-50">
                    <span className="min-w-0 truncate text-slate-900">{p.pharmacy_name || "Pharmacie"}</span>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-2xs font-semibold ${healthTone(p.health).chip}`}>{HEALTH_LABELS[p.health]}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </BlockShell>
  );
}

// --- Missions ----------------------------------------------------------------------------------------------------------------
export function MissionsBlock({ version }) {
  const state = useBlock("missions", version);
  if (state.phase === "gone") return null;
  return (
    <BlockShell title="Missions" link={{ to: "/missions", label: "Missions" }} state={state} testid="dashboard-missions">
      {(data) => (
        <div>
          <p className="text-xs text-slate-500 mb-1">{data.scope === "all" ? "Toutes les missions" : "Mes missions"}</p>
          <Figure value={data.today} label="aujourd'hui" to="/missions?tab=planning" testid="dashboard-missions-today" />
          <Figure value={data.this_week} label="cette semaine" to="/missions?tab=planning" testid="dashboard-missions-week" />
          <Figure value={data.in_progress} label="en cours" to="/missions" testid="dashboard-missions-running" />
          {data.procedures_incomplete !== null && (
            <Figure value={data.procedures_incomplete} tone="warn" to="/missions" testid="dashboard-missions-procedures"
              label="en cours avec une procédure obligatoire non terminée" />
          )}
          {data.reports_to_review !== null && data.reports_to_review !== undefined && (
            <Figure value={data.reports_to_review} tone="warn" to="/missions?tab=review" testid="dashboard-missions-reports" label="comptes rendus à valider" />
          )}
        </div>
      )}
    </BlockShell>
  );
}

// --- Temps -------------------------------------------------------------------------------------------------------------------
export function TimeBlock({ version }) {
  const state = useBlock("time", version);
  if (state.phase === "gone") return null;
  return (
    <BlockShell title="Temps de la semaine" link={{ to: "/missions?tab=time", label: "Détail" }} state={state} testid="dashboard-time">
      {(data) => (
        <div>
          <p className="text-xs text-slate-500 mb-1">
            {data.all ? `Toute l'équipe · ${plural(data.people, "personne", "personnes")}` : "Mes heures pointées"}
          </p>
          <div className="font-display font-bold text-xl text-slate-900 tabular-nums" data-testid="dashboard-time-total">
            {data.minutes.total ? formatMinutes(data.minutes.total) : "Aucun pointage"}
          </div>
          {data.minutes.total > 0 && (
            <ul className="mt-2 space-y-1">
              {ACTIVITIES.filter((a) => data.minutes[a]).map((a) => (
                <li key={a} className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">{ACTIVITY_LABELS[a]}</span>
                  <span className="font-semibold tabular-nums text-slate-900">{formatMinutes(data.minutes[a])}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </BlockShell>
  );
}

// --- Matériel ----------------------------------------------------------------------------------------------------------------
export function EquipmentBlock({ version }) {
  const state = useBlock("equipment", version);
  if (state.phase === "gone") return null;
  return (
    <BlockShell title="Matériel" link={{ to: "/equipements", label: "Anomalies" }} state={state} testid="dashboard-equipment">
      {(data) => (
        <div className="space-y-2">
          <p className="text-xs text-slate-500">{data.all ? "Toutes les pharmacies" : "Mes anomalies et celles de mes missions"}</p>
          {data.open === 0 ? (
            <p className="inline-flex items-center gap-2 text-sm text-slate-600" data-testid="dashboard-equipment-none">
              <CheckCircle2 size={16} className="text-emerald-600" aria-hidden="true" />Aucune anomalie ouverte.
            </p>
          ) : (
            <>
              <HBars testid="dashboard-equipment-chart" rows={[...SEVERITIES].reverse().map((sev, i) => ({
                id: sev, label: SEVERITY_META[sev].label, value: data.by_severity[sev] || 0, color: [STATUS.critical, STATUS.serious, STATUS.warning, STATUS.neutral][Math.min(i, 3)] }))} />
              <div className="flex flex-wrap gap-1.5" data-testid="dashboard-equipment-severity">
                {[...SEVERITIES].reverse().filter((s) => data.by_severity[s]).map((s) => (
                  <Link key={s} to={`/equipements?gravite=${s}`}
                    className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${SEVERITY_META[s].className} hover:opacity-80`}>
                    {data.by_severity[s]} {SEVERITY_META[s].label.toLowerCase()}
                  </Link>
                ))}
              </div>
              <Figure value={data.overdue} label="en retard sur leur échéance" tone="warn" to="/equipements?retard=1" testid="dashboard-equipment-overdue" />
              <Figure value={data.mine} label="dont je suis responsable" to="/equipements?responsable=moi" testid="dashboard-equipment-mine" />
            </>
          )}
        </div>
      )}
    </BlockShell>
  );
}

// --- Formations ---------------------------------------------------------------------------------------------------------------
export function TrainingBlock({ version }) {
  const state = useBlock("training", version);
  if (state.phase === "gone") return null;
  return (
    <BlockShell title="Formations" link={{ to: "/formations", label: "Sessions" }} state={state} testid="dashboard-training">
      {(data) => (
        <div className="space-y-1">
          <Figure value={data.this_week} label={data.this_week > 1 ? "sessions cette semaine" : "session cette semaine"} to="/formations"
            testid="dashboard-training-week" />
          <Figure value={data.to_close} label={data.to_close > 1 ? "sessions passées encore à clôturer" : "session passée encore à clôturer"} tone="warn"
            to="/formations" testid="dashboard-training-to-close" />
          <Figure value={data.mine_upcoming} label="à venir où je suis formateur" to="/formations" state={{ sessions: "mine" }} testid="dashboard-training-mine" />
        </div>
      )}
    </BlockShell>
  );
}

// --- Clients (Ordre 07: the portfolio by phase, health and reviews) -----------------------------------------------------------
export function CustomersBlock({ version }) {
  const state = useBlock("customers", version);
  if (state.phase === "gone") return null;
  return (
    <BlockShell title="Clients" link={{ to: "/clients", label: "Portefeuille" }} state={state} testid="dashboard-customers">
      {(data) => (
        <div className="space-y-1">
          <div className="mb-2">
            <Donut testid="dashboard-customers-chart" centerLabel="clients" rows={Object.keys(PHASE_LABELS).map((phase) => ({
              id: phase, label: PHASE_LABELS[phase], value: data.by_phase[phase] || 0 }))} />
          </div>
          <FigureGrid>
          {["to_start", "onboarding", "live"].map((phase) => (
            <Figure key={phase} value={data.by_phase[phase]} label={PHASE_LABELS[phase].toLowerCase()}
              to={`/clients?group=${phase === "live" ? "live" : "deploying"}&phase=${phase}`} testid={`dashboard-customers-${phase}`} />
          ))}
          <Figure value={data.to_activate} label="à déclarer en service" tone="warn" to="/clients?group=deploying&phase=onboarding_done"
            testid="dashboard-customers-to-activate" />
          <Figure value={data.at_risk} label="à risque" tone="warn" to="/clients?health=at_risk" testid="dashboard-customers-at-risk" />
          {data.attention > 0 && (
            <Figure value={data.attention} label="à surveiller" to="/clients?health=attention" testid="dashboard-customers-attention" />
          )}
          {data.review_overdue > 0 && (
            <Figure value={data.review_overdue} label={data.review_overdue > 1 ? "revues dépassées" : "revue dépassée"} tone="warn"
              to="/clients?review=overdue" testid="dashboard-customers-review" />
          )}
          {data.suspended > 0 && (
            <Figure value={data.suspended} label={data.suspended > 1 ? "suspendus" : "suspendu"} tone="warn" to="/clients?group=live&status=suspended"
              testid="dashboard-customers-suspended" />
          )}
          {data.all && <Figure value={data.mine} label="dont je suis responsable" to="/clients?manager=me" testid="dashboard-customers-mine" />}
          </FigureGrid>
        </div>
      )}
    </BlockShell>
  );
}

// --- Revenus (Ordre 17, D-71): per currency, never added across currencies -----------------------------------------------------
const perCurrency = (totals) => Object.entries(totals || {}).sort(([a], [b]) => a.localeCompare(b)).map(([c, v]) => formatMinor(v, c)).join(" · ");

export function BillingBlock({ version }) {
  const state = useBlock("billing", version);
  if (state.phase === "gone") return null;
  return (
    <BlockShell title="Revenus" link={{ to: "/revenus/abonnements", label: "Abonnements" }} state={state} testid="dashboard-billing">
      {(data) => {
        const overdue = Object.fromEntries(Object.entries(data.overdue || {}).map(([c, b]) => [c, Object.values(b).reduce((s, v) => s + v, 0)]));
        return (
          <div className="space-y-2">
            <div className="text-sm" data-testid="dashboard-billing-mrr">
              <span className="text-slate-500">Revenu mensuel récurrent : </span>
              <span className="font-semibold text-slate-900 tabular-nums">{perCurrency(data.mrr) || "—"}</span>
            </div>
            <div className="text-sm" data-testid="dashboard-billing-collected">
              <span className="text-slate-500">Encaissé ce mois : </span>
              <span className="font-semibold text-slate-900 tabular-nums">{perCurrency(data.collected_month) || "—"}</span>
            </div>
            <Figure value={data.overdue_count} label={data.overdue_count > 1 ? "échéances en retard" : "échéance en retard"} tone={data.overdue_count ? "warn" : undefined}
              to="/revenus/factures?tab=overdue" testid="dashboard-billing-overdue" />
            {data.overdue_count > 0 && <p className="text-xs text-slate-500 tabular-nums -mt-1 pl-1">Reste dû : {perCurrency(overdue)}</p>}
            <Figure value={data.to_invoice} label={data.to_invoice > 1 ? "échéances à facturer" : "échéance à facturer"} to="/revenus/factures" testid="dashboard-billing-to-invoice" />
            {data.trials_ended > 0 && (
              <Figure value={data.trials_ended} label={data.trials_ended > 1 ? "essais terminés" : "essai terminé"} tone="warn"
                to="/revenus/abonnements?trial_ended=1" testid="dashboard-billing-trials" />
            )}
          </div>
        );
      }}
    </BlockShell>
  );
}

// --- Documents et contrats (Ordre 18, D-73) --------------------------------------------------------------------------------------
export function RecordsBlock({ version }) {
  const state = useBlock("records", version);
  if (state.phase === "gone") return null;
  return (
    <BlockShell title="Documents" link={{ to: "/documents/obligations", label: "Obligations" }} state={state} testid="dashboard-records">
      {(data) => (
        <div className="space-y-1">
          <Figure value={data.obligations_overdue} label={data.obligations_overdue > 1 ? "obligations en retard" : "obligation en retard"}
            tone={data.obligations_overdue ? "warn" : undefined} to="/documents/obligations?vue=overdue" testid="dashboard-records-overdue" />
          <Figure value={data.obligations_30_days} label="à faire dans les 30 jours" to="/documents/obligations" testid="dashboard-records-30-days" />
          <Figure value={data.my_obligations_open} label="dont je suis responsable" to="/documents/obligations?vue=mine" testid="dashboard-records-mine" />
          <Figure value={data.expiring_60_days} label={data.expiring_60_days > 1 ? "documents expirent dans 60 jours" : "document expire dans 60 jours"}
            to="/documents?view=expiring" testid="dashboard-records-expiring" />
          {data.in_review > 0 && <Figure value={data.in_review} label="en revue" to="/documents?view=review" testid="dashboard-records-review" />}
        </div>
      )}
    </BlockShell>
  );
}

// --- Achats et actifs (Ordre 20-4): a figure is null when the person lacks the key of its gesture, and is then not shown -------
export function ProcurementBlock({ version }) {
  const state = useBlock("procurement", version);
  if (state.phase === "gone") return null;
  return (
    <BlockShell title="Achats et actifs" link={{ to: "/achats/demandes", label: "Demandes d'achat" }} state={state} testid="dashboard-procurement">
      {(data) => {
        const has = (key) => data[key] != null;
        const rows = [
          has("to_approve") && <Figure key="a" value={data.to_approve} label={data.to_approve > 1 ? "demandes à approuver" : "demande à approuver"} to="/achats/demandes?scope=to_approve" testid="dashboard-procurement-approve" />,
          has("to_order") && <Figure key="o" value={data.to_order} label={data.to_order > 1 ? "demandes approuvées à commander" : "demande approuvée à commander"} to="/achats/demandes?scope=to_order" testid="dashboard-procurement-order" />,
          has("to_receive") && <Figure key="r" value={data.to_receive} label="en attente de livraison" to="/achats/demandes?scope=to_receive" testid="dashboard-procurement-receive" />,
          has("delivery_late") && data.delivery_late > 0 && <Figure key="l" value={data.delivery_late} tone="warn" label={data.delivery_late > 1 ? "livraisons en retard" : "livraison en retard"} to="/achats/demandes?scope=to_receive" testid="dashboard-procurement-late" />,
          has("to_invoice") && <Figure key="i" value={data.to_invoice} label="à facturer" to="/achats/demandes?scope=to_invoice" testid="dashboard-procurement-invoice" />,
          has("my_requests_open") && <Figure key="m" value={data.my_requests_open} label="de mes demandes en cours" to="/achats/demandes?scope=mine" testid="dashboard-procurement-mine" />,
          has("loans_overdue") && <Figure key="p" value={data.loans_overdue} tone="warn" label={data.loans_overdue > 1 ? "prêts en retard" : "prêt en retard"} to="/achats/actifs?overdue=1" testid="dashboard-procurement-loans" />,
          has("warranties_ending") && <Figure key="w" value={data.warranties_ending} label="garanties finissent sous 60 jours" to="/achats/actifs?warranty=ending" testid="dashboard-procurement-warranties" />,
          has("renewals_ending") && <Figure key="n" value={data.renewals_ending} label="renouvellements sous 60 jours" to="/achats/actifs" testid="dashboard-procurement-renewals" />,
          has("my_assets") && <Figure key="y" value={data.my_assets} label="équipements à ma charge" to="/achats/mon-materiel" testid="dashboard-procurement-assets" />,
        ].filter(Boolean);
        return (
          <div className="space-y-1">
            <FigureGrid>{rows}</FigureGrid>
            {data.inventory_open && <p className="text-xs text-slate-600 pt-1" data-testid="dashboard-procurement-inventory"><Link to="/achats/inventaire" className="underline">Un inventaire est ouvert</Link></p>}
          </div>
        );
      }}
    </BlockShell>
  );
}

// --- Marketing (Ordre 21-4): aggregate counts for every reader; the cost figure only when the server sent it ---------------------
export function MarketingBlock({ version }) {
  const state = useBlock("marketing", version);
  if (state.phase === "gone") return null;
  return (
    <BlockShell title="Marketing" link={{ to: "/marketing/campagnes", label: "Campagnes" }} state={state} testid="dashboard-marketing">
      {(data) => (
        <div className="space-y-1">
          <Figure value={data.leads} label={`${data.leads > 1 ? "leads" : "lead"} en ${data.window_days} jours${data.conversion_rate !== null ? ` · ${formatRate(data.conversion_rate)} devenus clients` : ""}`} to="/marketing/indicateurs" testid="dashboard-marketing-leads" />
          <Figure value={data.campaigns_active} label={data.campaigns_active > 1 ? "campagnes en cours" : "campagne en cours"} to="/marketing/campagnes" testid="dashboard-marketing-campaigns" />
          {data.campaigns_ending > 0 && <Figure value={data.campaigns_ending} label={data.campaigns_ending > 1 ? "campagnes se terminent sous 14 jours" : "campagne se termine sous 14 jours"} to="/marketing/campagnes" testid="dashboard-marketing-ending" />}
          {data.campaigns_to_close > 0 && <Figure value={data.campaigns_to_close} tone="warn" label={data.campaigns_to_close > 1 ? "campagnes à terminer (fin dépassée)" : "campagne à terminer (fin dépassée)"} to="/marketing/campagnes" testid="dashboard-marketing-close" />}
          {data.without_origin > 0 && <Figure value={data.without_origin} label={data.without_origin > 1 ? "nouvelles pharmacies sans origine enregistrée" : "nouvelle pharmacie sans origine enregistrée"} to="/prospects" testid="dashboard-marketing-without-origin" />}
          {data.cost_per_lead && Object.keys(data.cost_per_lead).length > 0 && (
            <p className="text-xs text-slate-600 pt-1" data-testid="dashboard-marketing-cpl">
              Coût par lead : {Object.entries(data.cost_per_lead).map(([c, minor]) => (minor === null ? `— ${c}` : formatMinor(minor, c))).join(" · ")}
            </p>
          )}
        </div>
      )}
    </BlockShell>
  );
}

// --- Gouvernance (Ordre 22-5): each figure appears only when the server sent it (its list's visibility and the reader's rights) ---------------
export function GovernanceBlock({ version }) {
  const state = useBlock("governance", version);
  if (state.phase === "gone") return null;
  return (
    <BlockShell title="Gouvernance" link={{ to: "/gouvernance/decisions", label: "Décisions" }} state={state} testid="dashboard-governance">
      {(data) => {
        const { decisions, meetings, risks, knowledge, late_actions: late } = data;
        const critical = risks ? (risks.by_level.critical || 0) + (risks.by_level.high || 0) : 0;
        const nothing = !decisions && !knowledge;
        return (
          <div className="space-y-1">
            {decisions && (
              <>
                <Figure value={decisions.to_adopt} label={decisions.to_adopt > 1 ? "décisions attendent votre arbitrage" : "décision attend votre arbitrage"} to="/gouvernance/decisions?view=to_adopt" testid="dashboard-governance-to-adopt" />
                <Figure value={decisions.undecided} label={decisions.undecided > 1 ? "décisions non tranchées" : "décision non tranchée"} to="/gouvernance/decisions?view=undecided" testid="dashboard-governance-undecided" />
                {decisions.to_review > 0 && <Figure value={decisions.to_review} tone="warn" label={decisions.to_review > 1 ? "décisions différées à revoir" : "décision différée à revoir"} to="/gouvernance/decisions?view=undecided" testid="dashboard-governance-to-review" />}
              </>
            )}
            {risks && (
              <>
                <div className="py-2">
                  <StackedBar testid="dashboard-governance-chart" rows={["critical", "high", "medium", "low"].map((lvl) => ({
                    id: lvl, label: LEVEL_LABELS[lvl], value: risks.by_level[lvl] || 0,
                    color: { critical: STATUS.critical, high: STATUS.serious, medium: STATUS.warning, low: STATUS.good }[lvl] }))} />
                </div>
                <Figure value={risks.open} label={risks.open > 1 ? "risques ouverts" : "risque ouvert"} to="/gouvernance/risques" testid="dashboard-governance-risks" />
                {critical > 0 && (
                  <p className="text-xs text-slate-500 pl-0.5" data-testid="dashboard-governance-severe">
                    dont {risks.by_level.critical ? `${risks.by_level.critical} ${LEVEL_LABELS.critical.toLowerCase()}${risks.by_level.critical > 1 ? "s" : ""}` : ""}
                    {risks.by_level.critical && risks.by_level.high ? " et " : ""}
                    {risks.by_level.high ? `${risks.by_level.high} ${LEVEL_LABELS.high.toLowerCase()}${risks.by_level.high > 1 ? "s" : ""}` : ""}
                  </p>
                )}
                {risks.to_review > 0 && <Figure value={risks.to_review} tone="warn" label={risks.to_review > 1 ? "risques à revoir" : "risque à revoir"} to="/gouvernance/risques?review=1" testid="dashboard-governance-risks-review" />}
              </>
            )}
            {meetings && (meetings.next_7_days > 0 || meetings.next) && (
              <Figure value={meetings.next_7_days} label={`${meetings.next_7_days > 1 ? "réunions" : "réunion"} sous ${data.window_days} jours${meetings.next ? ` · prochaine : ${meetings.next.title}, ${formatMoment(meetings.next.starts_at)}` : ""}`}
                to={meetings.next ? `/gouvernance/reunions/${meetings.next.id}` : "/gouvernance/reunions"} testid="dashboard-governance-meetings" />
            )}
            {late != null && late > 0 && <Figure value={late} tone="warn" label={late > 1 ? "actions en retard (décisions, réunions, mesures)" : "action en retard (décisions, réunions, mesures)"} to="/tasks" testid="dashboard-governance-late" />}
            {knowledge && knowledge.to_review > 0 && <Figure value={knowledge.to_review} label={knowledge.to_review > 1 ? "articles à relire" : "article à relire"} to="/support/connaissances?view=to_review" testid="dashboard-governance-knowledge" />}
            {nothing && <p className="text-sm text-slate-500">Rien à suivre pour l'instant.</p>}
          </div>
        );
      }}
    </BlockShell>
  );
}

// --- Produit (Ordre 19, D-61): each figure appears only when the person holds the key of the gesture it asks for ---------------
export function ProductBlock({ version }) {
  const state = useBlock("product", version);
  if (state.phase === "gone") return null;
  return (
    <BlockShell title="Produit" link={{ to: "/produit", label: "Retours produit" }} state={state} testid="dashboard-product">
      {(data) => {
        const severe = (data.live_by_severity?.critical || 0) + (data.live_by_severity?.major || 0);
        return (
          <div className="space-y-1">
            {data.live_incidents > 0 && (
              <div className="mb-2">
                <HBars testid="dashboard-product-chart" rows={[
                  { id: "critical", label: "Critiques", value: data.live_by_severity?.critical || 0, color: STATUS.critical },
                  { id: "major", label: "Majeurs", value: data.live_by_severity?.major || 0, color: STATUS.serious },
                  { id: "minor", label: "Mineurs", value: data.live_by_severity?.minor || 0, color: STATUS.neutral },
                ]} />
              </div>
            )}
            <Figure value={data.live_incidents} label={data.live_incidents > 1 ? "incidents en cours" : "incident en cours"} tone={severe ? "warn" : undefined}
              to="/produit/incidents?state=live" testid="dashboard-product-incidents" />
            {data.live_incidents > 0 && severe > 0 && (
              <p className="text-xs text-slate-500 pl-0.5" data-testid="dashboard-product-severe">
                dont {data.live_by_severity.critical ? `${data.live_by_severity.critical} critique${data.live_by_severity.critical > 1 ? "s" : ""}` : ""}
                {data.live_by_severity.critical && data.live_by_severity.major ? " et " : ""}
                {data.live_by_severity.major ? `${data.live_by_severity.major} majeur${data.live_by_severity.major > 1 ? "s" : ""}` : ""}
              </p>
            )}
            {data.postmortems_due != null && data.postmortems_due > 0 && (
              <Figure value={data.postmortems_due} label={data.postmortems_due > 1 ? "post-mortems en retard" : "post-mortem en retard"} tone="warn"
                to="/produit/incidents?state=resolved" testid="dashboard-product-postmortems" />
            )}
            {data.to_triage != null && (
              <Figure value={data.to_triage} label={data.to_triage > 1 ? "retours à qualifier" : "retour à qualifier"} to="/produit?status=proposed" testid="dashboard-product-triage" />
            )}
            {data.in_test != null && (
              <Figure value={data.in_test} label={data.in_test > 1 ? "retours en test" : "retour en test"} to="/produit?status=in_test" testid="dashboard-product-in-test" />
            )}
            {data.delivered_unchecked != null && data.delivered_unchecked > 0 && (
              <Figure value={data.delivered_unchecked} label={data.delivered_unchecked > 1 ? "livrés à vérifier" : "livré à vérifier"}
                to="/produit?status=delivered" testid="dashboard-product-unchecked" />
            )}
          </div>
        );
      }}
    </BlockShell>
  );
}

// --- Tickets (Ordre 08, D-51) ---------------------------------------------------------------------------------------------------
export function TicketsBlock({ version }) {
  const state = useBlock("tickets", version);
  if (state.phase === "gone") return null;
  return (
    <BlockShell title="Tickets" link={{ to: "/support/tickets", label: "Tickets" }} state={state} testid="dashboard-tickets">
      {(data) => (
        <div className="space-y-1">
          {data.weekly && (
            <div className="mb-3">
              <div className="mb-1 text-xs font-medium text-slate-500">Tickets créés et résolus par semaine</div>
              <WeekChart testid="dashboard-tickets-weekly" height={110} series={[
                { key: "created", label: "Créés", color: RAMP[1], points: data.weekly.created },
                { key: "resolved", label: "Résolus", color: STATUS.good, points: data.weekly.resolved }]} />
            </div>
          )}
          <div className="mb-2">
            <HBars testid="dashboard-tickets-chart" rows={[
              { id: "critical", label: "Critique", value: data.by_priority.critical || 0, color: STATUS.critical },
              { id: "high", label: "Haute", value: data.by_priority.high || 0, color: STATUS.serious },
              { id: "normal", label: "Normale", value: data.by_priority.normal || 0, color: STATUS.warning },
              { id: "low", label: "Basse", value: data.by_priority.low || 0, color: STATUS.neutral },
            ]} />
          </div>
          <FigureGrid>
          <Figure value={data.open} label={data.open > 1 ? "tickets ouverts" : "ticket ouvert"} to="/support/tickets?state=open" testid="dashboard-tickets-open" />
          {data.by_priority.critical > 0 && (
            <Figure value={data.by_priority.critical} label={data.by_priority.critical > 1 ? "critiques" : "critique"} tone="warn"
              to="/support/tickets?state=open&priority=critical" testid="dashboard-tickets-critical" />
          )}
          <Figure value={data.breached} label="délai dépassé" tone="warn" to="/support/tickets?delay=breached" testid="dashboard-tickets-breached" />
          {data.at_risk > 0 && <Figure value={data.at_risk} label="délai bientôt dépassé" to="/support/tickets?delay=at_risk" testid="dashboard-tickets-at-risk" />}
          <Figure value={data.unassigned} label="sans responsable" tone={data.unassigned ? "warn" : undefined} to="/support/tickets?responsible=none&state=open"
            testid="dashboard-tickets-unassigned" />
          {data.waiting_long > 0 && (
            <Figure value={data.waiting_long} label="en attente de la pharmacie depuis plus de 3 jours" to="/support/tickets?status=waiting_customer"
              testid="dashboard-tickets-waiting" />
          )}
          <Figure value={data.resolved_week} label="résolus cette semaine" to="/support/tickets?state=done" testid="dashboard-tickets-resolved" />
          {data.all && <Figure value={data.mine} label="dont je suis responsable" to="/support/tickets?responsible=me&state=open" testid="dashboard-tickets-mine" />}
          </FigureGrid>
          {data.partial && <p className="text-xs text-slate-500">Délais calculés sur les 2 000 premiers tickets ouverts.</p>}
        </div>
      )}
    </BlockShell>
  );
}

// --- Finance -----------------------------------------------------------------------------------------------------------------
// One line per currency: amounts are never converted nor added across currencies (the Finance module's rule).
function AmountsByCurrency({ rows, testid, suffix }) {
  return (
    <ul className="space-y-1" data-testid={testid}>
      {rows.map((r) => (
        <li key={r.currency} className="flex items-baseline justify-between gap-3 text-sm">
          <span className="font-display font-bold text-lg tabular-nums text-slate-900">{formatAmount(r.amount, r.currency)}</span>
          <span className="text-xs text-slate-500">{suffix(r)}</span>
        </li>
      ))}
    </ul>
  );
}

export function FinanceBlock({ version }) {
  const state = useBlock("finance", version);
  if (state.phase === "gone") return null;
  return (
    <BlockShell title="Finance" link={{ to: "/finance", label: "Finance" }} state={state} testid="dashboard-finance">
      {(data) => (
        <div className="space-y-3">
          {data.month !== null && (
            <div>
              <p className="text-xs text-slate-500 mb-1">Dépenses du mois</p>
              {data.month.length === 0
                ? <p className="text-sm text-slate-600" data-testid="dashboard-finance-month-none">Aucune dépense ce mois-ci.</p>
                : <AmountsByCurrency rows={data.month} testid="dashboard-finance-month" suffix={(r) => plural(r.count, "dépense", "dépenses")} />}
            </div>
          )}
          {data.claims_to_review !== null && (
            <Figure value={data.claims_to_review} label={data.claims_to_review > 1 ? "notes de frais à approuver" : "note de frais à approuver"}
              to="/finance?tab=review" testid="dashboard-finance-claims" />
          )}
          {data.reimbursements_due !== null && (
            <div>
              <p className="text-xs text-slate-500 mb-1">Remboursements dus</p>
              {data.reimbursements_due.length === 0
                ? <p className="text-sm text-slate-600" data-testid="dashboard-finance-due-none">Aucun remboursement en attente.</p>
                : (
                  <Link to="/finance?tab=contributions" className="block -mx-2 px-2 py-1 rounded-md hover:bg-slate-50">
                    <AmountsByCurrency rows={data.reimbursements_due} testid="dashboard-finance-due"
                      suffix={(r) => `à ${plural(r.people, "personne", "personnes")}`} />
                  </Link>
                )}
            </div>
          )}
        </div>
      )}
    </BlockShell>
  );
}

// --- Assistance --------------------------------------------------------------------------------------------------------------
export function AssistBlock({ version }) {
  const state = useBlock("assist", version);
  if (state.phase === "gone") return null;
  return (
    <BlockShell title="Assistance" link={{ to: "/assistance", label: "Assistance" }} state={state} testid="dashboard-assist">
      {(data) => (
        <div>
          <p className="text-xs text-slate-500 mb-1">{data.scope === "all" ? "Toute l'équipe" : "Mes assistances"}</p>
          {data.waiting !== null && (
            <Figure value={data.waiting} label={data.waiting > 1 ? "demandes en attente" : "demande en attente"} tone="warn"
              to="/assistance" testid="dashboard-assist-waiting" />
          )}
          <Figure value={data.this_week} label={`${data.this_week > 1 ? "assistances" : "assistance"} cette semaine${data.partial ? " (au moins)" : ""}`}
            to="/assistance?tab=history" testid="dashboard-assist-week" />
          {data.average_minutes !== null && (
            <Figure value={`${data.average_minutes} min`} label="en moyenne" testid="dashboard-assist-average" />
          )}
        </div>
      )}
    </BlockShell>
  );
}

// Block id -> component. An id this screen does not know yet (a newer server) is simply not shown.
export const BLOCK_COMPONENTS = {
  todo: TodoBlock,
  commercial: CommercialBlock,
  tasks: TasksBlock,
  onboarding: OnboardingBlock,
  pilots: PilotsBlock,
  customers: CustomersBlock,
  tickets: TicketsBlock,
  billing: BillingBlock,
  records: RecordsBlock,
  product: ProductBlock,
  procurement: ProcurementBlock,
  marketing: MarketingBlock,
  governance: GovernanceBlock,
  missions: MissionsBlock,
  time: TimeBlock,
  equipment: EquipmentBlock,
  training: TrainingBlock,
  finance: FinanceBlock,
  assist: AssistBlock,
};
